'use client';

import {
  type FocusEventHandler,
  type KeyboardEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { useMergeRefs } from '../../hooks/use-merge-refs';
import { Tooltip } from '../tooltip';
import { SliderRootContext } from './context/slider-root.context';
import { sliderHandleRecipe } from './styles/slider-handle.styles';
import type { SliderHandlePropsInterface } from './types/slider-handle.types';

const PAGE_KEYS = ['PageUp', 'PageDown'];
const END_KEYS = ['Home', 'End'];
const All_KEYS = new Set([
  ...PAGE_KEYS,
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  ...END_KEYS,
]);
const DIRECTION_KEYS = {
  increase: ['PageUp', 'ArrowUp', 'ArrowRight'],
  decrease: ['PageDown', 'ArrowDown', 'ArrowLeft'],
};

/**
 * The Slider's handle
 *
 * @since 10.0.0
 */
export const SliderHandle = forwardRef<HTMLDivElement, SliderHandlePropsInterface>(
  (props: SliderHandlePropsInterface, forwardedRef) => {
    const {
      ariaLabel: ariaLabelContext,
      value,
      min,
      max,
      handles,
      setValue,
      step,
      disabled,
      shiftStep,
      reverse,
      forceUpdateDependency,
      ...context
    } = useContext(SliderRootContext);

    const {
      ariaLabel = ariaLabelContext,
      ariaValuetext,
      className,
      css: cssProp,
      onKeyDown,
      style,
      onFocus,
      onBlur,
      tooltipText,
      tooltipPortalId,
      tooltipPortalRoot,
      ...rest
    } = props;

    // State is used over a ref in order to trigger a re-render when the handle is in the DOM
    const [handle, setHandle] = useState<HTMLElement>();
    const [open, setOpen] = useState<boolean>(false);

    const setHandleRef = useCallback(
      (node: HTMLElement) => {
        if (node) {
          if (!handles.has(node)) {
            handles.add(node);
          }
          setHandle(node);
        }
      },
      [handles],
    );

    const refs = useMergeRefs([forwardedRef, setHandleRef]);

    const styles = css(sliderHandleRecipe.raw({ disabled, reverse }), cssProp);

    const handleIndex = useMemo(() => {
      return [...handles].findIndex((el) => el === handle);
    }, [handle, handles]);

    const handleValue = useMemo(() => {
      if (!handle) {
        return 0;
      }

      if (typeof value === 'number') {
        return value;
      }

      return value[handleIndex];
    }, [value, handle, handleIndex]);

    const position = useMemo(() => {
      if (!handleValue) {
        return '0%';
      }
      return `${100 * ((handleValue - min) / (max - min))}%`;
    }, [max, min, handleValue]);

    const positionProperty = useMemo(() => {
      return reverse ? 'insetInlineEnd' : 'insetInlineStart';
    }, [reverse]);

    const handleStep = useCallback(
      (key: string, shiftKey = false) => {
        const skipStep = typeof shiftStep === 'function' ? shiftStep(step) : (shiftStep ?? step);
        const increment = shiftKey || PAGE_KEYS.includes(key) ? skipStep : step;

        if (DIRECTION_KEYS[reverse ? 'decrease' : 'increase'].includes(key)) {
          setValue(handleValue + increment, handleIndex);
        } else if (DIRECTION_KEYS[reverse ? 'increase' : 'decrease'].includes(key)) {
          setValue(handleValue - increment, handleIndex);
        }
      },
      [shiftStep, step, reverse, setValue, handleValue, handleIndex],
    );

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        onKeyDown?.(event);

        if (!All_KEYS.has(event.key)) return;
        if (event.shiftKey && END_KEYS.includes(event.key)) return;

        // Prevents scrolling
        event.preventDefault();

        if (event.key === 'Home') {
          setValue(min, handleIndex, { endKey: true });
        } else if (event.key === 'End') {
          setValue(max, handleIndex, { endKey: true });
        } else {
          handleStep(event.key, event.shiftKey);
        }
      },
      [handleStep, max, min, onKeyDown, setValue, handleIndex],
    );

    const handleFocus: FocusEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        onFocus?.(event);
        setOpen(true);
      },
      [setOpen, onFocus],
    );

    const handleBlur: FocusEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        onBlur?.(event);
        setOpen(false);
      },
      [setOpen, onBlur],
    );

    useEffect(() => {
      return () => {
        if (handle && handles.has(handle)) {
          handles.delete(handle);
        }
      };
    }, [handle, handles]);

    const ariaLabelContent = useMemo(() => {
      if (typeof ariaLabel !== 'function') {
        return ariaLabel;
      }

      return ariaLabel({ value, index: handleIndex, valueNow: handleValue });
    }, [ariaLabel, handleIndex, handleValue, value]);

    const ariaValueContent = useMemo(() => {
      const text = ariaValuetext ?? context.ariaValuetext;
      if (typeof text !== 'function') {
        return text;
      }

      return text({ value, index: handleIndex, valueNow: handleValue });
    }, [ariaValuetext, context.ariaValuetext, handleIndex, handleValue, value]);

    const tooltipContent = useMemo(() => {
      const text = tooltipText ?? context.tooltipText;
      if (typeof text !== 'function') {
        return text;
      }

      return text({ value, index: handleIndex, valueNow: handleValue });
    }, [tooltipText, context.tooltipText, handleIndex, handleValue, value]);

    return (
      <Tooltip
        forceUpdateDependency={forceUpdateDependency}
        open={open}
        trigger={
          <div
            aria-disabled={disabled}
            aria-label={ariaLabelContent}
            aria-orientation="horizontal"
            aria-valuenow={handleValue}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuetext={ariaValueContent}
            className={cx('c11n', 'c11n_slider__handle', styles, className)}
            data-c11n-component="Slider.Handle"
            data-c11n-version={__C11N_VERSION__}
            onKeyDown={disabled ? undefined : handleKeyDown}
            ref={refs}
            role="slider"
            style={{
              [positionProperty]: position,
              ...style,
            }}
            tabIndex={disabled ? -1 : 0}
            onBlur={disabled ? undefined : handleBlur}
            onFocus={disabled ? undefined : handleFocus}
            {...rest}
          />
        }
        content={tooltipContent ? String(tooltipContent) : String(handleValue)}
        focusManagerProps={{ disabled: true }}
        placement={context.tooltipPlacement}
        portalId={tooltipPortalId}
        portalRoot={tooltipPortalRoot}
        useRoleProps={{ enabled: false }}
      />
    );
  },
);

SliderHandle.displayName = 'Slider.Handle';
